import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/FormField'
import { Combobox } from '@/components/Combobox'
import { RegionalDateField } from '@/components/RegionalDateField'
import { Toolbar } from '@/components/Toolbar'
import { useToast } from '@/hooks/useToast'
import { useStartOnboarding } from '@/features/inbox/queries'
import { SYNC_INTERVAL_MINUTES } from '@/features/inbox/types'
import type {
  ClassificationMode,
  ImportHorizon,
  ImportScope,
  InboxAccount,
  MailSource,
  SyncInterval,
} from '@/features/inbox/types'

const HORIZON_OPTIONS: { value: ImportHorizon; label: string }[] = [
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '12mo', label: 'Last 12 months' },
  { value: 'all', label: 'Everything' },
  { value: 'custom', label: 'Custom date range' },
]

const SCOPE_OPTIONS: { value: ImportScope; label: string; description: string }[] = [
  { value: 'all', label: 'All mail', description: 'Import and sort every email.' },
  {
    value: 'financial_only',
    label: 'Financial only',
    description: 'Only invoices, bills, receipts, statements and emails with PDF attachments.',
  },
  {
    value: 'keywords',
    label: 'Only emails matching my keywords',
    description: 'Import only emails whose subject or sender contains a keyword you choose.',
  },
]

const MODE_OPTIONS: { value: ClassificationMode; label: string; description: string }[] = [
  {
    value: 'ai',
    label: 'AI classification',
    description: 'Best accuracy. The AI sorts every email and uses your AI quota.',
  },
  {
    value: 'keyword',
    label: 'Keyword rules only',
    description: 'No AI cost. Watch-rule keywords sort the mail; the rest goes to Other.',
  },
  {
    value: 'hybrid',
    label: 'Hybrid',
    description: 'Keyword rules first, then AI for whatever the keywords miss.',
  },
]

const SOURCE_OPTIONS: { value: MailSource; label: string; description: string }[] = [
  {
    value: 'inbox',
    label: 'Inbox only',
    description: 'Just the emails currently in your Inbox folder.',
  },
  {
    value: 'inbox_sent',
    label: 'Inbox + Sent',
    description: 'Your Inbox plus the mail you have sent.',
  },
  {
    value: 'all',
    label: 'All mail',
    description: 'Everything in the mailbox, including archived mail.',
  },
]

const SYNC_OPTIONS: { value: SyncInterval; label: string }[] = [
  { value: 'manual', label: 'Manual only' },
  { value: '15min', label: 'Every 15 minutes' },
  { value: 'hourly', label: 'Every hour' },
  { value: '6h', label: 'Every 6 hours' },
  { value: 'daily', label: 'Every day' },
]

function parseKeywords(raw: string): string[] {
  return raw
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean)
}

export function OnboardingCard({
  account,
  canOnboard,
}: {
  account: InboxAccount
  canOnboard: boolean
}) {
  const { toast } = useToast()
  const startMutation = useStartOnboarding()

  const [source, setSource] = useState<MailSource>('inbox')
  const [horizon, setHorizon] = useState<ImportHorizon>('90d')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [scope, setScope] = useState<ImportScope>('all')
  const [keywords, setKeywords] = useState('')
  const [mode, setMode] = useState<ClassificationMode>('ai')
  const [syncPreset, setSyncPreset] = useState<SyncInterval>('manual')

  const validate = (): string | null => {
    if (horizon === 'custom' && !customStart && !customEnd) {
      return 'Pick a start or end date for the custom range.'
    }
    if (scope === 'keywords' && parseKeywords(keywords).length === 0) {
      return 'Add at least one keyword, or choose a different scope.'
    }
    return null
  }

  const filterBody = () => ({
    horizon,
    custom_start: horizon === 'custom' ? customStart || null : null,
    custom_end: horizon === 'custom' ? customEnd || null : null,
    scope,
    source,
    keywords: scope === 'keywords' ? parseKeywords(keywords) : undefined,
  })

  const onStart = async () => {
    const err = validate()
    if (err) {
      toast({ title: err, variant: 'destructive' })
      return
    }
    try {
      await startMutation.mutateAsync({
        id: account.id,
        body: {
          ...filterBody(),
          classification_mode: mode,
          sync_interval_minutes:
            syncPreset === 'manual' ? 1440 : SYNC_INTERVAL_MINUTES[syncPreset],
          auto_sync_enabled: syncPreset !== 'manual',
        },
      })
      toast({ title: 'Import started', description: 'Your mailbox is importing in the background.' })
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not start the import'
      toast({ title: 'Could not start import', description: msg, variant: 'destructive' })
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="size-4 text-primary" />
          Set up your mailbox import
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Choose how far back to import from <span className="font-medium">{account.email}</span>,
          what to pull in, and how it should be sorted. Importing runs in the background.
        </p>

        <FormField
          label="Import from"
          control={
            <Combobox<MailSource>
              value={source}
              onChange={(v) => setSource((v as MailSource) ?? 'inbox')}
              options={SOURCE_OPTIONS}
            />
          }
          hint={SOURCE_OPTIONS.find((s) => s.value === source)?.description}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label="How far back"
            control={
              <Combobox<ImportHorizon>
                value={horizon}
                onChange={(v) => setHorizon((v as ImportHorizon) ?? '90d')}
                options={HORIZON_OPTIONS}
              />
            }
          />
          <FormField
            label="Sort emails using"
            control={
              <Combobox<ClassificationMode>
                value={mode}
                onChange={(v) => setMode((v as ClassificationMode) ?? 'ai')}
                options={MODE_OPTIONS}
              />
            }
            hint={MODE_OPTIONS.find((m) => m.value === mode)?.description}
          />
        </div>

        {horizon === 'custom' ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <RegionalDateField
              label="From"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
            />
            <RegionalDateField
              label="To"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
            />
          </div>
        ) : null}

        <FormField
          label="What to import"
          control={
            <Combobox<ImportScope>
              value={scope}
              onChange={(v) => setScope((v as ImportScope) ?? 'all')}
              options={SCOPE_OPTIONS}
            />
          }
          hint={SCOPE_OPTIONS.find((s) => s.value === scope)?.description}
        />

        {scope === 'keywords' ? (
          <FormField
            label="Keywords (comma separated, any match)"
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            placeholder="e.g. invoice, receipt, statement, acme"
          />
        ) : null}

        <FormField
          label="After the import, keep syncing"
          control={
            <Combobox<SyncInterval>
              value={syncPreset}
              onChange={(v) => setSyncPreset((v as SyncInterval) ?? 'manual')}
              options={SYNC_OPTIONS}
            />
          }
        />

        <p className="text-xs text-muted-foreground">
          The import runs in the background - you can keep using the app and watch progress on
          this card. Very large mailboxes import the newest mail first.
        </p>

        <Toolbar align="end">
          <Button onClick={onStart} disabled={startMutation.isPending || !canOnboard}>
            {startMutation.isPending ? 'Starting…' : 'Start import'}
          </Button>
        </Toolbar>
        {!canOnboard ? (
          <p className="text-xs text-muted-foreground">
            You need the inbox onboarding permission to start an import.
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}
